import * as React from "react";
import { z } from "zod";
import { useForm, FormProvider, useFormState } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../../../components/ui/button";
import { Icons } from "../../../utils/icons";
import { useToast } from "../../../components/ui/use-toast";
import SectionFields403b from "../components/403b/section-fields"; // Import form fields component
import XanoAPI from "../../../lib/XanoAPI";
import { ClientType } from "../../../types";
import type { AutoSaveStatus } from "../../../hooks/useIntervalAutoSave";
import { Clock, Sparkles } from "lucide-react";

// Define the schema for the 403(b) form with prefixed fields
const financialInformation403bSchema = z.object({
  t_403b_currentAge: z.number().min(0, "Age must be positive"),
  t_403b_plannedRetirementAge: z
    .number()
    .min(0, "Retirement age must be positive"),
  t_403b_lifeExpectancy: z.number().min(0, "Life expectancy must be positive"),
  t_403b_currentBalance: z.number().min(0, "Balance must be positive"),
  t_403b_annualContribution: z.number().min(0, "Contribution must be positive"),
  t_403b_employerMatchPercentage: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_employerMatchCap: z.number().min(0, "Cap must be positive"),
  t_403b_expectedAnnualReturnRate: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_inflationRate: z.number().min(0).max(100, "Must be between 0 and 100"),
  t_403b_contributionFrequency: z.enum(["monthly", "bi-weekly"]),
  t_403b_annualSalary: z.number().min(0, "Salary must be positive"),
  t_403b_contributionPercentage: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_desiredAnnualIncomeInRetirement: z
    .number()
    .min(0, "Income must be positive"),
  t_403b_withdrawalRate: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_payoutPeriod: z.number().min(1, "Payout period must be at least 1"),
  t_403b_startWithdrawalAge: z.number().min(0, "Age must be positive"),
  t_403b_marginalTaxRateInRetirement: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_stateTaxRate: z.number().min(0).max(100, "Must be between 0 and 100"),
  t_403b_postRetirementReturnRate: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_socialSecurityIncome: z.number().min(0, "Income must be positive"),
  t_403b_otherRetirementIncomeSources: z
    .number()
    .min(0, "Income must be positive"),
  t_403b_earlyWithdrawalPenaltyPercentage: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_ageAtEarlyWithdrawal: z.number().min(0, "Age must be positive"),
  t_403b_exemptionsFromEarlyWithdrawalPenalty: z.boolean(),
  t_403b_irsContributionLimit: z.number().min(0, "Limit must be positive"),
  t_403b_catchUpContributions: z
    .number()
    .min(0, "Contribution must be positive"),
  t_403b_rmdStartAge: z.number().min(0, "Age must be positive"),
  t_403b_beneficiaryInfoForRMDs: z.boolean(),
  t_403b_loanBalance: z.number().min(0, "Loan balance must be positive"),
  t_403b_loanRepaymentTerm: z.number().min(0, "Term must be positive"),
  t_403b_loanInterestRate: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_investmentMix: z.string().optional(),
  t_403b_expenseRatio: z.number().min(0).max(100, "Must be between 0 and 100"),
  t_403b_administrativeFees: z.number().min(0, "Fees must be positive"),
  t_403b_employerContributionVestingPercentage: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
  t_403b_yearsToFullVesting: z.number().min(0, "Years must be positive"),
  t_403b_colaPercentage: z
    .number()
    .min(0)
    .max(100, "Must be between 0 and 100"),
});

type FormData403b = z.infer<typeof financialInformation403bSchema>;

const FinancialInformation403b = ({
  client,
  onUpdate,
  setFormsDirty,
  fetchClientData,
  resetFormsDirtyState,
  handleTabChange,
  formsDirty,
  saveStatus,
  lastSaveTime,
  isSmartPasteOpen,
  setIsSmartPasteOpen,
  setSmartPasteHandlers,
}: {
  client: ClientType;
  onUpdate: Function;
  setFormsDirty: Function;
  fetchClientData: Function;
  resetFormsDirtyState: Function;
  handleTabChange: Function;
  formsDirty: any;
  saveStatus: AutoSaveStatus;
  lastSaveTime: number;
  isSmartPasteOpen?: boolean;
  setIsSmartPasteOpen?: (open: boolean) => void;
  setSmartPasteHandlers?: (handlers: any) => void;
}) => {
  const { toast } = useToast();
  const api = XanoAPI.getInstance();
  const isAutoSaving = React.useRef(false);
  
  // Smart Paste operation flag - prevents form reset during population
  const isSmartPastePopulating = React.useRef(false);
  
  // Refs for handler functions to avoid stale closures
  const handlersRef = React.useRef({
    onPersonalDataParsed: (data: Partial<ClientType>) => {},
    onFinancialDataParsed: (data: Partial<ClientType>) => {},
    onPopulateSingleField: (fieldName: string, value: any) => {},
    onSave: async () => {},
  });

  // Initialize react-hook-form with defaultValues
  const form = useForm<FormData403b>({
    resolver: zodResolver(financialInformation403bSchema),
    mode: "onChange", // Enable real-time dirty tracking
    criteriaMode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      t_403b_currentAge: client?.t_403b_currentAge || 0,
      t_403b_plannedRetirementAge: client?.t_403b_plannedRetirementAge || 65,
      t_403b_lifeExpectancy: client?.t_403b_lifeExpectancy || 85,
      t_403b_currentBalance: client?.t_403b_currentBalance || 0,
      t_403b_annualContribution: client?.t_403b_annualContribution || 0,
      t_403b_employerMatchPercentage:
        client?.t_403b_employerMatchPercentage || 0,
      t_403b_employerMatchCap: client?.t_403b_employerMatchCap || 0,
      t_403b_expectedAnnualReturnRate:
        client?.t_403b_expectedAnnualReturnRate || 5,
      t_403b_inflationRate: client?.t_403b_inflationRate || 3,
      t_403b_contributionFrequency:
        client?.t_403b_contributionFrequency || "monthly",
      t_403b_annualSalary: client?.t_403b_annualSalary || 0,
      t_403b_contributionPercentage: client?.t_403b_contributionPercentage || 0,
      t_403b_desiredAnnualIncomeInRetirement:
        client?.t_403b_desiredAnnualIncomeInRetirement || 0,
      t_403b_withdrawalRate: client?.t_403b_withdrawalRate || 4,
      t_403b_payoutPeriod: client?.t_403b_payoutPeriod || 30,
      t_403b_startWithdrawalAge: client?.t_403b_startWithdrawalAge || 65,
      t_403b_marginalTaxRateInRetirement:
        client?.t_403b_marginalTaxRateInRetirement || 15,
      t_403b_stateTaxRate: client?.t_403b_stateTaxRate || 5,
      t_403b_postRetirementReturnRate:
        client?.t_403b_postRetirementReturnRate || 5,
      t_403b_socialSecurityIncome: client?.t_403b_socialSecurityIncome || 0,
      t_403b_otherRetirementIncomeSources:
        client?.t_403b_otherRetirementIncomeSources || 0,
      t_403b_earlyWithdrawalPenaltyPercentage:
        client?.t_403b_earlyWithdrawalPenaltyPercentage || 10,
      t_403b_ageAtEarlyWithdrawal: client?.t_403b_ageAtEarlyWithdrawal || 0,
      t_403b_exemptionsFromEarlyWithdrawalPenalty:
        client?.t_403b_exemptionsFromEarlyWithdrawalPenalty || false,
      t_403b_irsContributionLimit: client?.t_403b_irsContributionLimit || 0,
      t_403b_catchUpContributions: client?.t_403b_catchUpContributions || 0,
      t_403b_rmdStartAge: client?.t_403b_rmdStartAge || 72,
      t_403b_beneficiaryInfoForRMDs:
        client?.t_403b_beneficiaryInfoForRMDs || false,
      t_403b_loanBalance: client?.t_403b_loanBalance || 0,
      t_403b_loanRepaymentTerm: client?.t_403b_loanRepaymentTerm || 0,
      t_403b_loanInterestRate: client?.t_403b_loanInterestRate || 0,
      t_403b_investmentMix: client?.t_403b_investmentMix || "stocks",
      t_403b_expenseRatio: client?.t_403b_expenseRatio || 0,
      t_403b_administrativeFees: client?.t_403b_administrativeFees || 0,
      t_403b_employerContributionVestingPercentage:
        client?.t_403b_employerContributionVestingPercentage || 100,
      t_403b_yearsToFullVesting: client?.t_403b_yearsToFullVesting || 0,
      t_403b_colaPercentage: client?.t_403b_colaPercentage || 0,
    },
  });

  const { handleSubmit, control, reset, formState, trigger, setValue } = form;

  // Access formState properties directly for reactivity
  const { isValid, isDirty, dirtyFields } = formState;

  // Register Smart Paste handlers with parent (must be at top level, not conditional)
  React.useEffect(() => {
    if (setSmartPasteHandlers) {
      console.log('📝 [403b Tab] Registering Smart Paste handlers');
      setSmartPasteHandlers((prev: any) => ({
        ...prev,
        financial: {
          onPersonalDataParsed: (data: any) => {
            console.log('📞 [403b Tab] onPersonalDataParsed wrapper called');
            return handlersRef.current.onPersonalDataParsed(data);
          },
          onFinancialDataParsed: (data: any) => {
            console.log('📞 [403b Tab] onFinancialDataParsed wrapper called');
            return handlersRef.current.onFinancialDataParsed(data);
          },
          onPopulateSingleField: (fieldName: string, value: any) => {
            console.log('📞 [403b Tab] onPopulateSingleField wrapper called');
            return handlersRef.current.onPopulateSingleField(fieldName, value);
          },
          onSave: () => {
            console.log('📞 [403b Tab] onSave wrapper called');
            return handlersRef.current.onSave();
          },
          isSaveDisabled: !isValid || !isDirty,
        },
      }));
      console.log('✅ [403b Tab] Handlers registered successfully');
    }
  }, [setSmartPasteHandlers, isValid, isDirty]);  // Include isValid/isDirty to update isSaveDisabled

  // Reset form when client changes
  React.useEffect(() => {
    if (client?.id && !isSmartPastePopulating.current) {
      reset({
        t_403b_currentAge: client?.t_403b_currentAge || 0,
        t_403b_plannedRetirementAge: client?.t_403b_plannedRetirementAge || 65,
        t_403b_lifeExpectancy: client?.t_403b_lifeExpectancy || 85,
        t_403b_currentBalance: client?.t_403b_currentBalance || 0,
        t_403b_annualContribution: client?.t_403b_annualContribution || 0,
        t_403b_employerMatchPercentage:
          client?.t_403b_employerMatchPercentage || 0,
        t_403b_employerMatchCap: client?.t_403b_employerMatchCap || 0,
        t_403b_expectedAnnualReturnRate:
          client?.t_403b_expectedAnnualReturnRate || 5,
        t_403b_inflationRate: client?.t_403b_inflationRate || 3,
        t_403b_contributionFrequency:
          client?.t_403b_contributionFrequency || "monthly",
        t_403b_annualSalary: client?.t_403b_annualSalary || 0,
        t_403b_contributionPercentage:
          client?.t_403b_contributionPercentage || 0,
        t_403b_desiredAnnualIncomeInRetirement:
          client?.t_403b_desiredAnnualIncomeInRetirement || 0,
        t_403b_withdrawalRate: client?.t_403b_withdrawalRate || 4,
        t_403b_payoutPeriod: client?.t_403b_payoutPeriod || 30,
        t_403b_startWithdrawalAge: client?.t_403b_startWithdrawalAge || 65,
        t_403b_marginalTaxRateInRetirement:
          client?.t_403b_marginalTaxRateInRetirement || 15,
        t_403b_stateTaxRate: client?.t_403b_stateTaxRate || 5,
        t_403b_postRetirementReturnRate:
          client?.t_403b_postRetirementReturnRate || 5,
        t_403b_socialSecurityIncome: client?.t_403b_socialSecurityIncome || 0,
        t_403b_otherRetirementIncomeSources:
          client?.t_403b_otherRetirementIncomeSources || 0,
        t_403b_earlyWithdrawalPenaltyPercentage:
          client?.t_403b_earlyWithdrawalPenaltyPercentage || 10,
        t_403b_ageAtEarlyWithdrawal: client?.t_403b_ageAtEarlyWithdrawal || 0,
        t_403b_exemptionsFromEarlyWithdrawalPenalty:
          client?.t_403b_exemptionsFromEarlyWithdrawalPenalty || false,
        t_403b_irsContributionLimit: client?.t_403b_irsContributionLimit || 0,
        t_403b_catchUpContributions: client?.t_403b_catchUpContributions || 0,
        t_403b_rmdStartAge: client?.t_403b_rmdStartAge || 72,
        t_403b_beneficiaryInfoForRMDs:
          client?.t_403b_beneficiaryInfoForRMDs || false,
        t_403b_loanBalance: client?.t_403b_loanBalance || 0,
        t_403b_loanRepaymentTerm: client?.t_403b_loanRepaymentTerm || 0,
        t_403b_loanInterestRate: client?.t_403b_loanInterestRate || 0,
        t_403b_investmentMix: client?.t_403b_investmentMix || "stocks",
        t_403b_expenseRatio: client?.t_403b_expenseRatio || 0,
        t_403b_administrativeFees: client?.t_403b_administrativeFees || 0,
        t_403b_employerContributionVestingPercentage:
          client?.t_403b_employerContributionVestingPercentage || 100,
        t_403b_yearsToFullVesting: client?.t_403b_yearsToFullVesting || 0,
        t_403b_colaPercentage: client?.t_403b_colaPercentage || 0,
      });
      setTimeout(() => trigger(), 0);
    }
  }, [client?.id, reset, trigger]);

  // Sync React Hook Form's isDirty state with parent's formsDirty state
  React.useEffect(() => {
    if (isDirty && dirtyFields) {
      const dirtyFieldNames = Object.keys(dirtyFields);
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: dirtyFieldNames,
      }));
    } else {
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: [],
      }));
    }
  }, [isDirty, dirtyFields]);

  const onSubmit = async (data: FormData403b) => {
    if (!client || !client.id) {
      console.error("Client ID is missing.");
      return;
    }

    try {
      const processedData = {
        ...client,
        ...data,
      };

      await api.updateClient(client.id, processedData);
      await fetchClientData(client.id);
      reset(data);
      resetFormsDirtyState();

      // Only show toast notification on manual save, not auto-save
      if (!isAutoSaving.current) {
        toast({
          title: "403(b) Information Updated!",
          description:
            "Your client's 403(b) financial information has been updated.",
        });
      }
      
      // Reset the auto-save flag
      isAutoSaving.current = false;
    } catch (error) {
      console.error("Error updating 403(b) information:", error);
    }
  };

  // Handle single field population from Smart Paste
  const handlePopulateSingleField = (fieldName: string, value: any) => {
    isSmartPastePopulating.current = true;
    
    try {
      setValue(fieldName as any, value, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
      
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: [...(prev.financialInformation || []), fieldName],
      }));
      
      console.log(`✅ 403(b) field applied: ${fieldName} = ${value}`);
    } catch (err) {
      console.error(`Failed to apply 403(b) field ${fieldName}:`, err);
    }
    
    setTimeout(() => {
      isSmartPastePopulating.current = false;
    }, 100);
  };
  
  handlersRef.current.onPopulateSingleField = handlePopulateSingleField;

  // Handle Smart Paste - Financial Information
  const handleFinancialDataParsed = (parsedData: Partial<ClientType>) => {
    console.log("🤖 [403b Tab] Smart Paste: Populating", Object.keys(parsedData).length, "financial fields");
    console.log("   All keys:", Object.keys(parsedData));

    // Remove fields that require specific JSON structure
    const fieldsToRemove = ['deductions', 'additionalIncome', 'allocations', 'federal_tsp_allocations', 'unused_sick_leave'];
    fieldsToRemove.forEach(field => delete parsedData[field as keyof ClientType]);

    // Map common field names to 403b-specific field names
    const fieldMapping: Record<string, string> = {
      // Salary and pay periods
      'grossIncome': 't_403b_annualSalary',
      'annualGrossIncome': 't_403b_annualSalary',
      'salary': 't_403b_annualSalary',
      'currentSalary': 't_403b_annualSalary',
      'annualSalary': 't_403b_annualSalary',
      'payPeriodsAnnual': 't_403b_contributionFrequency', // Note: 403b uses frequency enum, not count
      'payPeriodsPerYear': 't_403b_contributionFrequency',
      
      // Contribution fields
      '403bContributionPercentage': 't_403b_contributionPercentage',
      'contributionPercentage': 't_403b_contributionPercentage',
      'contributionRate': 't_403b_contributionPercentage',
      '403bContributionRate': 't_403b_contributionPercentage',
      'annualContribution': 't_403b_annualContribution',
      
      // Employer match
      'employerMatchPercent': 't_403b_employerMatchPercentage',
      'employerMatch': 't_403b_employerMatchPercentage',
      'companyMatch': 't_403b_employerMatchPercentage',
      'companyMatchPercent': 't_403b_employerMatchPercentage',
      'employerMatchCap': 't_403b_employerMatchCap',
      
      // Existing balance
      '403bBalance': 't_403b_currentBalance',
      'existing403bValue': 't_403b_currentBalance',
      'current403bBalance': 't_403b_currentBalance',
      '403bCurrentBalance': 't_403b_currentBalance',
      'currentBalance': 't_403b_currentBalance',
      
      // Age fields
      'currentAge': 't_403b_currentAge',
      'age': 't_403b_currentAge',
      'plannedRetirementAge': 't_403b_plannedRetirementAge',
      'retirementAge': 't_403b_plannedRetirementAge',
      'lifeExpectancy': 't_403b_lifeExpectancy',
      'lifeExpectancyAge': 't_403b_lifeExpectancy',
    };

    // Create mapped data object
    const mappedData: Partial<ClientType> = {};
    const fieldsToSet = new Set<string>();

    Object.entries(parsedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Check if this field needs mapping
        const mappedKey = fieldMapping[key] || key;
        
        // Only map if it's a 403b field or already a valid 403b field
        if (mappedKey.startsWith('t_403b_') || key.startsWith('t_403b_')) {
          mappedData[mappedKey as keyof ClientType] = value;
          fieldsToSet.add(mappedKey);
          if (mappedKey !== key) {
            console.log(`   🔄 Mapped ${key} → ${mappedKey} = ${value}`);
          }
        } else {
          // Try direct mapping if it's already a valid form field
          mappedData[key as keyof ClientType] = value;
          fieldsToSet.add(key);
        }
      }
    });

    isSmartPastePopulating.current = true;
    
    // Populate mapped fields
    Object.entries(mappedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && fieldsToSet.has(key)) {
        try {
          setValue(key as any, value, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });
          console.log(`   ✅ Set ${key} = ${value}`);
        } catch (err) {
          console.error(`   ❌ Failed to set ${key}:`, err);
        }
      }
    });
    
    setTimeout(() => {
      trigger();
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: Array.from(fieldsToSet),
      }));
      isSmartPastePopulating.current = false;
      console.log("✅ [403b Tab] Smart Paste: Financial population complete");
      console.log("   Fields populated:", Array.from(fieldsToSet));
    }, 100);
  };
  
  handlersRef.current.onFinancialDataParsed = handleFinancialDataParsed;
  handlersRef.current.onPersonalDataParsed = () => {
    console.log("⚠️ Personal data parsed in 403(b) tab - ignored");
  };
  handlersRef.current.onSave = async () => {
    console.log("💾 Smart Paste Save triggered (403(b))");
    return handleSubmit(onSubmit)();
  };

  // Real-time auto-save text with live updates
  const [currentTime, setCurrentTime] = React.useState(Date.now());
  
  // Update current time every second for real-time display
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getAutoSaveText = () => {
    if (lastSaveTime === 0) return null;
    
    const timeSinceLastSave = currentTime - lastSaveTime;
    const seconds = Math.floor(timeSinceLastSave / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `Autosaved ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (seconds > 5) {
      return `Autosaved ${seconds} seconds ago`;
    } else if (seconds > 0) {
      return "Autosaved just now";
    }
    return null;
  };

  return (
    <div className="py-6 mb-8">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SectionFields403b client={client} />
          <div className="fixed bottom-0 right-0 m-3 z-50 flex items-center gap-3">
            {getAutoSaveText() && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-md border border-gray-200">
                <Clock className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-xs text-gray-600">
                  {getAutoSaveText()}
                </span>
              </div>
            )}
            {setIsSmartPasteOpen && (
              <Button
                type="button"
                variant="outline"
                className="p-3"
                onClick={() => setIsSmartPasteOpen(true)}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Smart Paste
              </Button>
            )}
            <Button
              type="submit"
              id="save-financial-information"
              className="p-3 bg-accent"
              disabled={!isValid || !isDirty}
              onClick={(e) => {
                // Check if this is a manual click (has isTrusted property)
                if (e.isTrusted) {
                  isAutoSaving.current = false;
                } else {
                  // Programmatic click (auto-save)
                  isAutoSaving.current = true;
                }
              }}
            >
              <Icons.save className="mr-2" />
              {isDirty ? "Save Changes" : "No Changes"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default FinancialInformation403b;
